import React, {useEffect, useRef, useState} from "react";
import BootstrapTable, {
  ColumnDescription,
  TableChangeHandler
} from "react-bootstrap-table-next";
import paginationFactory, {
  PaginationProvider
} from "react-bootstrap-table2-paginator";
import {CardBody} from "reactstrap";
import {SortOrder} from "../../../shared/enums/sortOrder";
import PaginatedDataTablePageButtons from "../paginatedDataTablePageButtons/paginatedDataTablePageButtons";
import "./paginatedDataTable.sass";
import classNames from "classnames";

export interface IPaginatedDataTableProps {
  /**
   * Data to be populated in the table.
   */
  data: any[];

  /**
   * The callback function which return anything that will be showed in the table when data is empty.
   */
  noDataIndication?: string | JSX.Element | (() => string | JSX.Element);

  /**
   * The columns to be populated in the table. The dataField property must be a field that exist in the data.
   */
  columns: ColumnDescription<any, any>[];

  /**
   * The column of the data that is unique. This should be the name of a property that is unique for each item in the dataset.
   */
  keyField: string;

  /**
   * The number of rows per page.
   */
  sizePerPage: number;

  /**
   * The event callback for the navigation to the previous page.
   */
  onPrevPage?: (pageNumber: number, pageSize: number) => void;

  /**
   * The event callback for the navigation to the next page.
   */
  onNextPage?: (pageNumber: number, pageSize: number) => void;

  /**
   * The event callback for the navigation to a specific page.
   */
  onPageNumberSelected?: (pageNumber: number, pageSize: number) => void;

  /**
   * When set to true, the table change events need to be handled manually, including sort, pagination, search, filtering, etc.
   * Default is false.
   */
  remote?: boolean;

  /**
   * The length of data. If this is provided, it will be used to generate page buttons instead of referring to the actual data length.
   * This must be provided when remote is set to true.
   */
  dataLength?: number;

  /**
   * The callback for table sort event. This callback will be called when remote is set to true.
   * If it is not provided, default sorting will be used.
   */
  onSort?: (
    sortField: string,
    sortOrder: SortOrder,
    pageNumber: number,
    sizePerPage: number
  ) => void;

  /**
   * The callback for table page change event. This callback will be called when remote is set to true.
   * If it is not provided, default pagination will be used.
   */
  onPageChange?: (
    pageNumber: number,
    sizePerPage: number,
    sortField: string,
    sortOrder: SortOrder
  ) => void;

  /**
   * The page number that should be displayed by the table currently.
   */
  currentPageNumber?: number;

  /**
   * True if border bottom should be hidden.
   */
  noBorderBottom?: boolean;

  /**
   * Function for applying dynamic classes to each row
   */
  rowClassFormatter?: (row: any, rowIndex: number) => string;
}

const PaginatedDataTable: React.FC<IPaginatedDataTableProps> = ({
  data,
  noDataIndication,
  columns,
  keyField,
  sizePerPage,
  onPrevPage,
  onNextPage,
  onPageNumberSelected,
  remote = false,
  dataLength,
  onSort,
  onPageChange,
  currentPageNumber,
  noBorderBottom,
  rowClassFormatter
}: IPaginatedDataTableProps) => {
  const [remotePaginatedData, setRemotePaginatedData] = useState(
    remote ? data.slice(0, sizePerPage) : data
  );
  const [localPaginationPageNumber, setLocalPaginationPageNumber] = useState(1);

  // Only set paginated data if remote is enabled.
  useEffect(() => {
    if (remote) {
      // if data.length is larger than sizePerPage, assume that the data provided is not paginated,
      // and we need to paginated the data. Otherwise, just take the data since it is paginated.
      const paginatedData =
        data.length > sizePerPage
          ? paginateUnpaginatedData(
              data,
              localPaginationPageNumber,
              sizePerPage
            )
          : data;
      setRemotePaginatedData(paginatedData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const table = useRef<any>();
  const pagination = paginationFactory({
    custom: true,
    sizePerPage: sizePerPage,
    totalSize: remote ? dataLength : data.length
  });

  if (
    currentPageNumber &&
    currentPageNumber > 0 &&
    currentPageNumber <=
      Math.ceil(
        pagination.options!.totalSize! / pagination.options!.sizePerPage!
      )
  ) {
    pagination.options!.page = currentPageNumber;
  }

  // This table change handler will be used if remote is enabled.
  const tableChangeHandler: TableChangeHandler<any> = (type, newState) => {
    setLocalPaginationPageNumber(newState.page);
    switch (type) {
      case "pagination":
        if (onPageChange !== undefined) {
          onPageChange(
            newState.page,
            newState.sizePerPage,
            newState.sortField,
            newState.sortOrder as SortOrder
          );
        } else {
          defaultPaginationHandler(data, newState.page, newState.sizePerPage);
        }
        break;

      case "sort":
        if (onSort !== undefined) {
          onSort(
            newState.sortField,
            newState.sortOrder as SortOrder,
            newState.page,
            newState.sizePerPage
          );
        } else {
          defaultSortHandler(
            data,
            newState.sortField,
            newState.sortOrder as SortOrder,
            newState.page,
            newState.sizePerPage
          );
        }
        break;
    }
  };

  // This pagination handler will be used if remote is enabled and onTableChange prop is not provided.
  const defaultPaginationHandler = (
    data: any[],
    pageNumber: number,
    sizePerPage: number
  ) => {
    setRemotePaginatedData(
      paginateUnpaginatedData(data, pageNumber, sizePerPage)
    );
  };

  /**
   * Paginate data by page number and page size. This is only applicable for local pagination where full data is provided,
   * since remote pagination returns paginated data.
   */
  const paginateUnpaginatedData = (
    data: any[],
    pageNumber: number,
    sizePerPage: number
  ): any[] => {
    const currentIndex = (pageNumber - 1) * sizePerPage;
    return data.slice(currentIndex, currentIndex + sizePerPage);
  };

  // This sort handler will be used if remote is enabled and onTableChange prop is not provided.
  const defaultSortHandler = (
    data: any[],
    sortField: string,
    sortOrder: SortOrder,
    pageNumber: number,
    sizePerPage: number
  ) => {
    let result = [];
    result = data.sort((a, b) => {
      return genericSort(
        a[sortField],
        b[sortField],
        sortOrder === SortOrder.Ascending
      );
    });
    const paginatedData = paginateUnpaginatedData(
      result,
      pageNumber,
      sizePerPage
    );
    setRemotePaginatedData(paginatedData);
  };

  const genericSort = (a: any, b: any, asc: boolean): number => {
    return a === b ? 0 : a > b ? (asc ? 1 : -1) : asc ? -1 : 1;
  };

  const internalRowClassFormatter = (row: any, rowIndex: number): string => {
    const rowClasses = rowClassFormatter?.(row, rowIndex);
    return rowClasses ?? "btn-reveal-trigger";
  };

  return (
    <CardBody className="paginatedDataTable" data-testid="paginatedDataTable">
      <PaginationProvider pagination={pagination}>
        {({paginationProps, paginationTableProps}) => {
          return (
            <>
              <div className="table-responsive">
                <BootstrapTable
                  noDataIndication={noDataIndication}
                  ref={table}
                  bootstrap4
                  // @ts-ignore
                  keyField={keyField}
                  // @ts-ignore
                  data={remote ? remotePaginatedData : data}
                  // @ts-ignore
                  columns={columns}
                  bordered={false}
                  classes={classNames({"no-border-bottom": noBorderBottom})}
                  rowClasses={internalRowClassFormatter}
                  remote={remote}
                  onTableChange={tableChangeHandler}
                  {...paginationTableProps}
                />
              </div>
              {paginationProps.totalSize! > sizePerPage && (
                <PaginatedDataTablePageButtons
                  pageButtonsRange={10}
                  paginationProps={paginationProps}
                  onPrevPage={onPrevPage}
                  onNextPage={onNextPage}
                  onPageNumberSelected={onPageNumberSelected}
                />
              )}
            </>
          );
        }}
      </PaginationProvider>
    </CardBody>
  );
};

export default PaginatedDataTable;
